import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import {HOMEPAGE_ANALYTICS_ACTIONS} from 'constants/analytics-products/HomepageAnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ReviewSummary} from 'constants/SourceConstants';
import usePreference from 'ui-library/hooks/usePreference';

import {HomepagePreferences} from '../HomepageConstants';
import {
  DEFAULT_REVIEW_STATUS_LABEL,
  ReviewItemStatusLabel,
  REVIEW_ITEM_STATUS_LABELS,
} from './MyWorkConstants';
import {MyWorkRowsFooterAction} from './MyWorkRowsList';
import {REVIEW_STATUS_METADATA} from './MyWorkStatusMetadata';
import MyWorkStatusTabs, {MyWorkStatusTabData} from './MyWorkStatusTabs';
import {getReviewStatusCount, getSelectedReviewSummaryIds, hasReviewSummaryWork} from './MyWorkUtils';
import {openMyWorkReviewableStatusSearch} from './openMyWorkSearch';

interface ReviewableRowsRenderProps {
  footerAction?: MyWorkRowsFooterAction;
  hasCategoryWork: boolean;
  selectedIds: Array<string> | null;
}

interface ReviewableMyWorkContentProps {
  ariaLabel: string;
  entityTypeLabel: string;
  modelTypes: Array<ModelTypeConstants>;
  preferenceKey: HomepagePreferences;
  reviewSummary: ReviewSummary | null;
  reviewStatusLabels?: ReadonlyArray<ReviewItemStatusLabel>;
  renderRows: (props: ReviewableRowsRenderProps) => JSX.Element;
}

/** Shared reviewable work queue with status tabs and status-specific search. */
export default function ReviewableMyWorkContent({
  ariaLabel,
  entityTypeLabel,
  modelTypes,
  preferenceKey,
  reviewSummary,
  reviewStatusLabels = REVIEW_ITEM_STATUS_LABELS,
  renderRows,
}: ReviewableMyWorkContentProps): JSX.Element {
  const [preferredStatus, setSelectedStatus] = usePreference<ReviewItemStatusLabel | undefined>(
    preferenceKey
  );
  const selectedStatus =
    preferredStatus != null && reviewStatusLabels.includes(preferredStatus)
      ? preferredStatus
      : reviewStatusLabels.includes(DEFAULT_REVIEW_STATUS_LABEL)
        ? DEFAULT_REVIEW_STATUS_LABEL
        : reviewStatusLabels[0];
  const hasCategoryWork = hasReviewSummaryWork(reviewSummary, reviewStatusLabels);
  const statuses: Array<MyWorkStatusTabData<ReviewItemStatusLabel>> = reviewStatusLabels.map(
    (statusLabel) => ({
      ...REVIEW_STATUS_METADATA[statusLabel],
      count: getReviewStatusCount(reviewSummary, statusLabel),
    })
  );

  return (
    <>
      {hasCategoryWork && (
        <MyWorkStatusTabs
          ariaLabel={ariaLabel}
          selectedStatus={selectedStatus}
          statuses={statuses}
          onStatusChange={(statusLabel) => {
            setSelectedStatus(statusLabel);
          }}
        />
      )}
      {renderRows({
        selectedIds: getSelectedReviewSummaryIds(reviewSummary, selectedStatus),
        hasCategoryWork,
        footerAction: hasCategoryWork
          ? {
              label: `See ${selectedStatus} ${entityTypeLabel} in search`,
              onClick: () => {
                trackEvent(HOMEPAGE_ANALYTICS_ACTIONS.CLICKED_MY_WORK_SEE_ALL_BUTTON);
                openMyWorkReviewableStatusSearch(modelTypes, selectedStatus);
              },
            }
          : undefined,
      })}
    </>
  );
}
